import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Paper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Line } from "react-chartjs-2";
import { Link } from "react-router-dom";
import {
  Chart as ChartJS,
  LineElement,
  LinearScale,
  CategoryScale, PointElement,
  Tooltip,
  Filler
} from "chart.js";
import { useDispatch, useSelector } from "react-redux";
import { fetchGraphData } from "../../../../store/reducers/graphDataSlice";


ChartJS.register(
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Filler
);

const scoringRulesPath = "/badges";

const AnalyticsChart = ({ userId, organizationId, style }) => {
  const [tab, setTab] = useState("weekly");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();

  const { weekly, monthly, loading, error } = useSelector(
    (state) => state.graphData || {
      weekly: { labels: [], points: [] },
      monthly: { labels: [], points: [] },
      loading: false,
      error: null,
    }
  );
  const { organizationDetails } = useSelector((state) => state.organization);
  const { customPoints } = useSelector((state) => state.badgesDetails);
  const evaluationDays = customPoints?.evaluation_time || "28";

  const orgColor = useMemo(() => {
    const color = (organizationDetails?.color || "").trim();
    const isValidHex = /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/.test(color);
    if (!isValidHex) return "#f6615f";
    if (color.length === 4) {
      return `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`;
    }
    return color;
  }, [organizationDetails]);

  const toRgba = (hex, alpha) => {
    const normalized = hex.replace("#", "");
    const r = parseInt(normalized.slice(0, 2), 16);
    const g = parseInt(normalized.slice(2, 4), 16);
    const b = parseInt(normalized.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Fetch graph data on mount or when user/org changes
  useEffect(() => {
    if (userId && organizationId) {
      dispatch(fetchGraphData({ userId, organizationId }));
    }
  }, [userId, organizationId, dispatch]);

  const selectedData = tab === "weekly" ? weekly : monthly;

  const data = {
    labels: selectedData.labels || [],
    datasets: [
      {
        label: "Points",
        data: selectedData.points || [],
        fill: true,
        backgroundColor: toRgba(orgColor, 0.2),
        borderColor: orgColor,
        tension: 0.4,
        pointRadius: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        ticks: {
          maxTicksLimit: isMobile ? 7 : 14,
          color: "black",
          font: {
            weight: "bold",
            size: 10,
          },
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => `${value} P`,
          color: "black",
          font: {
            weight: "bold",
          },
          stepSize: selectedData?.points?.length === 1 ? 1 : undefined,
          precision: 0,
        },
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: (context) => `${context.parsed.y} P`,
        },
      },
      legend: {
        display: false,
      },
    },
  };

  return (
    <Box style={style}>
      <Paper
        elevation={3}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          background: "var(--org-bg-tint-2, #fff7f6)",
        }}
      >
        <Box
          sx={{
            backgroundColor: orgColor,
            color: "#fff",
            px: isMobile ? 2 : 3,
            py: 1.5,
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", color: "#fff !important" }}
          >
            Analytics
          </Typography>
        </Box>

        <Box sx={{ p: isMobile ? 1.5 : 2 }}>
          <Box>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Tabs
                value={tab}
                onChange={(e, newValue) => setTab(newValue)}
                textColor="inherit"
                TabIndicatorProps={{
                  style: {
                    backgroundColor: orgColor,
                  },
                }}
                sx={{
                  "& .MuiTab-root": {
                    fontWeight: 600,
                    textTransform: "none",
                    minWidth: isMobile ? 72 : 90,
                  },
                  "& .Mui-selected": {
                    color: `${orgColor} !important`,
                  },
                }}
              >
                <Tab value="weekly" label="Weekly" />
                <Tab value="monthly" label="Monthly" />
              </Tabs>
            </Box>

            {loading ? (
              <Typography sx={{ mt: 2 }}>Loading...</Typography>
            ) : error ? (
              <Typography sx={{ mt: 2, color: "red" }}>{error}</Typography>
            ) : (
              <Box sx={{ height: { xs: 280, sm: 340, md: "40vh" }, mt: 2 }}>
                <Line data={data} options={options} />
              </Box>
            )}
          </Box>
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          p: isMobile ? 1.5 : 2,
          mt: 2,
          background: "var(--org-bg-tint-2, #fff7f6)",
          fontStyle: "italic",
          fontWeight: 600,
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 1.5,
            mb: 2,
          }}
        >
          <Box
            sx={{
              backgroundColor: "#fff",
              borderRadius: "10px",
              p: 2,
              border: "1px solid rgba(204, 204, 204, 0.8)",
              boxShadow: "0px 4px 18px rgba(0, 0, 0, 0.08)",
            }}
          >
            <Typography fontWeight={800} fontStyle="italic" mb={0.75}>
              * Current points:
            </Typography>
            <Typography fontSize={14} color="rgba(52, 53, 60, 1)" lineHeight={1.55}>
              Your points reflects your current level of learning and practice.
              Learning and practicing increase your score. If you're inactive for a
              while, your score gradually decreases as your skills and knowledge
              fade over time.
            </Typography>
          </Box>

          <Box
            sx={{
              backgroundColor: "#fff",
              borderRadius: "10px",
              p: 2,
              border: "1px solid rgba(204, 204, 204, 0.8)",
              boxShadow: "0px 4px 18px rgba(0, 0, 0, 0.08)",
            }}
          >
            <Typography fontWeight={800} fontStyle="italic" mb={0.75}>
              ** Badges Earned:
            </Typography>
            <Typography fontSize={14} color="rgba(52, 53, 60, 1)" lineHeight={1.55}>
              Each badge is time-stamped and captures your score level at that
              moment. Every 14 days, your current points are evaluated and a new
              badge is awarded. Keep practicing to earn the next one.
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            backgroundColor: "#fff",
            borderRadius: "10px",
            p: 2,
            boxShadow: "0px 4px 18px rgba(0, 0, 0, 0.08)",
          }}
        >
          <Typography fontSize={14} lineHeight={1.5}>
            <Link
              to={scoringRulesPath}
              style={{
                color: "var(--org-bg-color, #f6615f)",
                fontStyle: "italic",
                fontWeight: 700,
              }}
            >
              Read more about scoring rules here
            </Link>
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default AnalyticsChart;
